<template>
  <div data-testid="backup-and-restore-child-workspace-list" class="mt-6">
    <div class="coro-filter-content">
      <cui-filter-wrapper
        :show-clear-button="showClearFiltersButton"
        @clear-filters-clicked="clearFilters(() => $emit('filters-changed'))"
      >
        <div class="grid-container">
          <v-text-field
            v-model="localFilters.search"
            data-testid="backup-and-restore-child-workspaces-search"
            :class="{ 'filter-active': localFilters.search?.length }"
            density="compact"
            variant="outlined"
            :placeholder="$t('general.search')"
            :aria-label="$t('general.search')"
            prepend-inner-icon="icon-search-xs"
            rounded
            clearable
            hide-details
            clear-icon="$x"
          />
          <v-select
            v-model="localFilters.cloudApps"
            :items="cloudAppOptions"
            class="filter-menu"
            :class="{ 'filter-active': localFilters.cloudApps.length }"
            :placeholder="$t('cloudBackupAndRestore.filters.cloudApps')"
            :menu-props="{ maxHeight: '300' }"
            data-testid="backup-and-restore-child-workspaces-cloud-apps-filter"
            density="compact"
            variant="outlined"
            rounded
            hide-details
            multiple
          >
            <template #selection="{ index }">
              <span v-if="index === 0" class="body2">
                {{ $t("cloudBackupAndRestore.filters.cloudApps") }}
              </span>
            </template>
            <template #label>
              <span v-if="localFilters.cloudApps.length" class="filter-label">
                {{ localFilters.cloudApps.length }}
              </span>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props" :title="$t(`general.keepitWorkloads.${item.value}`)">
                <template #prepend="{ isActive }">
                  <v-list-item-action start>
                    <v-checkbox-btn density="compact" :model-value="isActive" />
                  </v-list-item-action>
                </template>
              </v-list-item>
            </template>
          </v-select>
          <v-select
            v-model="localFilters.connectionStatus"
            :items="connectionStatusOptions"
            class="filter-menu"
            :class="{ 'filter-active': localFilters.connectionStatus }"
            :placeholder="$t('cloudBackupAndRestore.filters.connectionStatus')"
            data-testid="backup-and-restore-child-workspaces-connection-status-filter"
            density="compact"
            variant="outlined"
            rounded
            hide-details
          >
            <template #selection="{ item }">
              {{ $t(`cloudBackupAndRestore.filters.connectionStatusOptions.${item.value}`) }}
            </template>
            <template #item="{ item, props }">
              <v-list-item
                v-bind="props"
                :title="$t(`cloudBackupAndRestore.filters.connectionStatusOptions.${item.value}`)"
              />
            </template>
          </v-select>
          <v-select
            v-model="localFilters.healthStatus"
            :items="healthStatusOptions"
            class="filter-menu"
            :class="{ 'filter-active': localFilters.healthStatus }"
            :placeholder="$t('cloudBackupAndRestore.filters.healthStatus')"
            data-testid="backup-and-restore-child-workspaces-health-status-filter"
            density="compact"
            variant="outlined"
            rounded
            hide-details
          >
            <template #selection="{ item }">
              {{ $t(`cloudBackupAndRestore.filters.healthStatusOptions.${item.value}`) }}
            </template>
            <template #item="{ item, props }">
              <v-list-item
                v-bind="props"
                :title="$t(`cloudBackupAndRestore.filters.healthStatusOptions.${item.value}`)"
              />
            </template>
          </v-select>
        </div>
      </cui-filter-wrapper>
    </div>

    <backup-and-restore-child-workspaces-table
      :items="items"
      :pagination="pagination"
      :total-items="totalItems"
      :loading="loading"
      :show-actions="showActions"
      @action-applied="onActionApplied"
      @page-changed="$emit('page-changed', $event)"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { CuiFilterWrapper } from "@coronet/cui";
import BackupAndRestoreChildWorkspacesTable from "@/components/tables/BackupAndRestoreChildWorkspacesTable.vue";
import EditCustomerAccountModal from "@/components/modals/msp/cloud-backup/EditCustomerAccountModal.vue";
import {
  type UpdateBackupChildPropertiesPayload,
  useMspCloudBackupStore,
} from "@/_store/msp-cloud-backup.module.ts";
import { useDialogsStore } from "@/_store/dialogs.module";
import { useFilters } from "@/composables/useFilters.ts";
import { FilterContext } from "@/_store/filters.module";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers";
import { ModalWidth } from "@/constants/modals";
import { RolePermissionsScope } from "@/constants/roles.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import {
  BackupAndRestoreAction,
  BackupChildAction,
  BackupChildConnectionStatus,
  BackupChildHealthStatus,
  KeepitWorkload,
  type BackupChild,
  type BackupChildActionPayload,
} from "@/constants/cloud-backup.ts";
import type { Pagination } from "@/types";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";

export default defineComponent({
  components: {
    BackupAndRestoreChildWorkspacesTable,
    CuiFilterWrapper,
  },
  props: {
    items: {
      type: Array as PropType<BackupChild[]>,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["filters-changed", "page-changed", "refresh"],
  setup(_props, { emit }) {
    const backupAndRestoreStore = useMspCloudBackupStore();
    const dialogsStore = useDialogsStore();

    const { localFilters, showClearFiltersButton, clearFilters } = useFilters({
      context: FilterContext.BACKUP_AND_RESTORE_CHILD_WORKSPACES,
      searchKey: "search",
      onFilterChange: () => emit("filters-changed"),
    });

    const showActions = computed(
      () =>
        !isMspActionRestricted(
          RolePermissionsScope.PROTECTION,
          SubscriptionModule.BACKUP_AND_RESTORE
        )
    );

    const openEditCustomerAccount = (item: BackupChild) => {
      dialogsStore.openDialog(
        componentDialogsConfigConstructor({
          action: BackupAndRestoreAction.EDIT_CUSTOMER_ACCOUNT,
          component: EditCustomerAccountModal,
          width: ModalWidth.LARGE,
          item,
          disable: !showActions.value,
          callback: async (payload: UpdateBackupChildPropertiesPayload) => {
            await backupAndRestoreStore.updateChildProperties(item.workspaceId, payload);
            emit("refresh");
          },
        })
      );
    };

    const onActionApplied = ({ action, item }: BackupChildActionPayload) => {
      if (action === BackupChildAction.VIEW_IN_KEEPIT) {
        backupAndRestoreStore.autologin(item.workspaceId);
        return;
      }

      openEditCustomerAccount(item);
    };

    return {
      localFilters,
      showActions,
      showClearFiltersButton,
      clearFilters,
      cloudAppOptions: Object.values(KeepitWorkload),
      connectionStatusOptions: [
        BackupChildConnectionStatus.CONNECTED,
        BackupChildConnectionStatus.NOT_CONNECTED,
      ],
      healthStatusOptions: [
        BackupChildHealthStatus.HEALTHY,
        BackupChildHealthStatus.UNHEALTHY,
        BackupChildHealthStatus.CRITICAL,
      ],
      onActionApplied,
    };
  },
});
</script>

<style scoped lang="scss">
.grid-container {
  display: grid;
  grid-template-columns: minmax(160px, 280px) repeat(3, minmax(160px, 200px));
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
}

:deep(*) {
  .v-slide-group__wrapper {
    z-index: 40 !important;
  }

  .filter-wrapper__content {
    flex-grow: 0 !important;
  }
}
</style>
